import { cn } from '@/utils';
import type { OffsetOptions, Placement } from '@floating-ui/react';
import { useBoolean } from 'ahooks';
import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import {
  PortalToFollowElem,
  PortalToFollowElemContent,
  PortalToFollowElemTrigger,
} from '../components/portal-to-follow-elem';

/**
 * Props for the Tooltip component
 */
export interface TooltipProps {
  /** Placement of the tooltip */
  position?: Placement;
  /** Method to trigger the tooltip */
  triggerMethod?: 'hover' | 'click';
  /** Content to be displayed in the tooltip */
  popupContent: React.ReactNode;
  /** Child element that triggers the tooltip */
  children: React.ReactNode;
  /** Whether to hide the arrow */
  hideArrow?: boolean;
  /** Additional className for the popup */
  popupClassName?: string;
  /** Offset options for positioning */
  offset?: OffsetOptions;
}

// SVG arrow for the tooltip
const arrow = (
  <svg
    className="absolute text-white h-2 w-full left-0 top-full"
    x="0px"
    y="0px"
    viewBox="0 0 255 255"
  >
    <polygon className="fill-current" points="0,0 127.5,127.5 255,0"></polygon>
  </svg>
);

/**
 * Tooltip component that displays content on hover or click
 */
const Tooltip: FC<TooltipProps> = ({
  position = 'top',
  triggerMethod = 'hover',
  popupContent,
  children,
  hideArrow,
  popupClassName,
  offset,
}) => {
  // State to control tooltip visibility
  const [open, setOpen] = useState(false);

  // State and refs to track hover states
  const [isHoverPopup, { setTrue: setHoverPopup, setFalse: setNotHoverPopup }] =
    useBoolean(false);
  const isHoverPopupRef = useRef(isHoverPopup);

  const [
    isHoverTrigger,
    { setTrue: setHoverTrigger, setFalse: setNotHoverTrigger },
  ] = useBoolean(false);
  const isHoverTriggerRef = useRef(isHoverTrigger);

  // Update refs when hover states change
  useEffect(() => {
    isHoverPopupRef.current = isHoverPopup;
  }, [isHoverPopup]);

  useEffect(() => {
    isHoverTriggerRef.current = isHoverTrigger;
  }, [isHoverTrigger]);

  /**
   * Handle mouse leave events
   * @param isTrigger Whether the leave event is from the trigger element
   */
  const handleLeave = (isTrigger: boolean) => {
    if (isTrigger) setNotHoverTrigger();
    else setNotHoverPopup();

    // Give time to move to the popup before closing
    setTimeout(() => {
      if (!isHoverPopupRef.current && !isHoverTriggerRef.current)
        setOpen(false);
    }, 100);
  };

  return (
    <PortalToFollowElem
      open={open}
      onOpenChange={setOpen}
      placement={position}
      offset={offset ?? 10}
    >
      <PortalToFollowElemTrigger
        onClick={() => triggerMethod === 'click' && setOpen((v) => !v)}
        onMouseEnter={() => {
          if (triggerMethod === 'hover') {
            setHoverTrigger();
            setOpen(true);
          }
        }}
        onMouseLeave={() => triggerMethod === 'hover' && handleLeave(true)}
      >
        {children}
      </PortalToFollowElemTrigger>
      <PortalToFollowElemContent className="z-[9999]">
        <div
          className={cn(
            'relative px-3 py-2 text-xs font-normal text-gray-700 bg-white rounded-md shadow-lg',
            popupClassName,
          )}
          onMouseEnter={() => triggerMethod === 'hover' && setHoverPopup()}
          onMouseLeave={() => triggerMethod === 'hover' && handleLeave(false)}
        >
          {popupContent}
          {!hideArrow && arrow}
        </div>
      </PortalToFollowElemContent>
    </PortalToFollowElem>
  );
};

export default React.memo(Tooltip);
